import React, { useEffect, useState, useRef } from "react";
import { useParams, Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { fetchBlogBySlug } from "../../services/api";
import RelatedBlogs from "../../components/Blogs/RelatedBlogs/RelatedBlogs";
import Ads from "../../components/Ads/Ads";
import "../AllBlogs/BlogShared.css";
import "./BlogPost.css";

const BlogPost: React.FC = () => {
  const { slug, category } = useParams();
  const [blog, setBlog]           = useState<any>(null);
  const [loading, setLoading]     = useState(true);
  const [readPct, setReadPct]     = useState(0);
  const contentRef                = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!slug) return;
    setLoading(true);
    fetchBlogBySlug(slug)
      .then(res => setBlog(res.data))
      .catch(err => console.error(err))
      .finally(() => setLoading(false));
  }, [slug]);

  /* Reading progress bar */
  useEffect(() => {
    const onScroll = () => {
      if (!contentRef.current) return;
      const el    = contentRef.current;
      const top   = el.getBoundingClientRect().top;
      const total = el.offsetHeight;
      const vh    = window.innerHeight;
      const scrolled = Math.max(0, -top);
      const pct = Math.min(100, Math.round((scrolled / (total - vh)) * 100));
      setReadPct(pct);
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, [blog]);

  const formattedDate = blog?.created_at
    ? new Date(blog.created_at).toLocaleDateString("en-IN", { day: "numeric", month: "long", year: "numeric" })
    : "";

  return (
    <>
      {/* {blog && (
        <Helmet>
          <title>{blog.header_title} — Acadebility</title>
          <meta name="description" content={blog.header_title} />
        </Helmet>
      )} */}
      <Helmet>
        {/* AdSense — loaded only on blog post pages */}
        <script
          async
          src="https://pagead2.googlesyndication.com/pagead/js/adsbygoogle.js?client=ca-pub-7684933817684584"
          crossOrigin="anonymous"
        />
        {blog && <title>{blog.header_title} — Acadebility</title>}
        {blog && <meta name="description" content={blog.header_title} />}
      </Helmet>

      {/* Reading progress bar */}
      <div className="bp-progress-bar">
        <div className="bp-progress-fill" style={{ width: `${readPct}%` }} />
      </div>

      {/* ── Hero ── */}
      <div className="bl-hero">
        <div className="bl-hero-inner">
          <nav className="bl-breadcrumb">
            <Link to="/">Home</Link><span>/</span>
            <Link to={`/blogs/all-blogs/${category || "posts"}`}>All Posts</Link>
            <span>/</span>
            <span className="bl-breadcrumb-current">
              {blog?.header_title || slug}
            </span>
          </nav>
          <h1 className="bl-hero-title">
            {loading ? "Loading article…" : blog?.header_title}
          </h1>
          {blog && (
            <div className="bl-hero-pills">
              {blog.posted_by && <div className="bl-hero-pill">✍️ {blog.posted_by}</div>}
              {formattedDate && <div className="bl-hero-pill">📅 {formattedDate}</div>}
              {blog.views != null && <div className="bl-hero-pill">👁 {blog.views} Views</div>}
              {blog.likes != null && <div className="bl-hero-pill">👍 {blog.likes} Likes</div>}
            </div>
          )}
        </div>
        <div className="bl-hero-blob bl-hero-blob-1" />
        <div className="bl-hero-blob bl-hero-blob-2" />
      </div>

      {/* ── Page body ── */}
      <div className="bl-page">
        <div className="bl-layout">

          {/* ── Main ── */}
          <main className="bl-main">
            {loading ? (
              <div className="bp-skeleton">
                <div className="bp-sk-line bp-sk-long" />
                <div className="bp-sk-line bp-sk-med" />
                <div className="bp-sk-line bp-sk-short" />
                <div className="bp-sk-block" />
                <div className="bp-sk-line bp-sk-long" />
                <div className="bp-sk-line bp-sk-med" />
              </div>
            ) : !blog ? (
              <div className="bp-error">
                <span>😕</span>
                <h3>Article not found</h3>
                <p>This article may have been moved or removed.</p>
                <Link to="/" className="bp-error-btn">Back to Home</Link>
              </div>
            ) : (
              <article className="bl-card bp-article" ref={contentRef}>
                {/* Article header */}
                <div className="bp-article-head">
                  <div className="bp-author-row">
                    <div className="bp-author-avatar">
                      {(blog.posted_by?.[0] || "A").toUpperCase()}
                    </div>
                    <div>
                      <div className="bp-author-name">{blog.posted_by || "Acadebility"}</div>
                      <div className="bp-author-date">{formattedDate}</div>
                    </div>
                    <div className="bp-article-stats">
                      <span>👁 {blog.views ?? 0}</span>
                      <span>👍 {blog.likes ?? 0}</span>
                    </div>
                  </div>
                </div>

                {/* Featured image */}
                {blog.image && (
                  <div className="bp-featured-img-wrap">
                    <img
                      src={blog.image}
                      alt={blog.header_title}
                      className="bp-featured-img"
                      loading="lazy"
                    />
                  </div>
                )}

                {/* Content */}
                <div
                  className="bp-content"
                  dangerouslySetInnerHTML={{ __html: blog.content }}
                />

                {/* Footer */}
                <div className="bp-article-footer">
                  <div className="bp-footer-left">
                    <span className="bp-read-pct">📖 {readPct}% read</span>
                  </div>
                  <div className="bp-footer-actions">
                    <Link
                      to={`/blogs/all-blogs/${category || "posts"}`}
                      className="bp-back-btn"
                    >
                      ← Back to All Posts
                    </Link>
                  </div>
                </div>
              </article>
            )}
          </main>

          {/* ── Sidebar ── */}
          <aside className="bl-sidebar">
            <Ads slot="8537390283" />
            <RelatedBlogs />
          </aside>

        </div>
      </div>
    </>
  );
};

export default BlogPost;
